import React, { useState, useEffect } from 'react';
import { holidayAPI } from '../../services';
import { useAsyncAction } from '../../hooks';
import {
  Button,
  Modal,
  Input,
  Select,
  ErrorMessage,
  SuccessMessage,
  Badge,
  LoadingSpinner,
  Card,
} from '../UI';
import { Star, Plus, Edit, Trash2, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const HolidayManagement = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  const { loading: createLoading, execute: executeCreate } = useAsyncAction();
  const { loading: updateLoading, execute: executeUpdate } = useAsyncAction();
  const { loading: deleteLoading, execute: executeDelete } = useAsyncAction();
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  
  const [form, setForm] = useState({
    date: '',
    name: '',
    description: '',
    type: 'company'
  });

  const holidayTypes = [
    { value: 'national', label: 'National Holiday' },
    { value: 'regional', label: 'Regional Holiday' },
    { value: 'company', label: 'Company Holiday' },
    { value: 'optional', label: 'Optional Holiday' }
  ];

  const getTypeColor = (type) => {
    const colors = {
      national: 'bg-red-100 text-red-700',
      regional: 'bg-blue-100 text-blue-700',
      company: 'bg-purple-100 text-purple-700',
      optional: 'bg-gray-100 text-gray-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  useEffect(() => {
    fetchHolidays();
  }, [currentYear]);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const response = await holidayAPI.getHolidays({ year: currentYear });
      if (response.status === 'success') {
        setHolidays(response.data.holidays || []);
      }
    } catch (err) {
      setError('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const result = await executeCreate(holidayAPI.createHoliday, form);
    if (result.success) {
      setSuccess('Holiday created successfully');
      setIsAddModalOpen(false);
      resetForm();
      fetchHolidays();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to create holiday');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const result = await executeUpdate(holidayAPI.updateHoliday, selected._id, form);
    if (result.success) {
      setSuccess('Holiday updated successfully');
      setIsEditModalOpen(false);
      fetchHolidays();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to update holiday');
    }
  };

  const handleDelete = async () => {
    const result = await executeDelete(holidayAPI.deleteHoliday, selected._id);
    if (result.success) {
      setSuccess('Holiday deleted successfully');
      setIsDeleteModalOpen(false);
      fetchHolidays();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to delete holiday');
    }
  };

  const resetForm = () => {
    setForm({ date: '', name: '', description: '', type: 'company' });
  };

  const openEditModal = (holiday) => {
    setSelected(holiday);
    setForm({
      date: holiday.date ? new Date(holiday.date).toISOString().split('T')[0] : '',
      name: holiday.name || '',
      description: holiday.description || '',
      type: holiday.type || 'company'
    });
    setIsEditModalOpen(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Group holidays by month
  const groupedHolidays = holidays.reduce((acc, holiday) => {
    const month = new Date(holiday.date).toLocaleString('en-US', { month: 'long' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(holiday);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Star className="w-6 h-6 text-purple-600" />
            Holiday Management
          </h2>
          <p className="text-gray-500">Manage company holidays for the calendar</p>
        </div>
        <Button onClick={() => { resetForm(); setIsAddModalOpen(true); }} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Holiday
        </Button>
      </div>

      {/* Year Navigation */}
      <Card className="p-4">
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => setCurrentYear(currentYear - 1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            <span className="text-xl font-bold text-gray-900">{currentYear}</span>
          </div>
          <button
            onClick={() => setCurrentYear(currentYear + 1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <div className="text-center mt-2 text-sm text-gray-500">
          {holidays.length} holiday{holidays.length !== 1 ? 's' : ''} in {currentYear}
        </div>
      </Card>

      {/* Holidays List */}
      {Object.keys(groupedHolidays).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedHolidays).map(([month, monthHolidays]) => (
            <Card key={month}>
              <div className="border-b border-gray-200 bg-gray-50 px-5 py-3 rounded-t-xl">
                <h3 className="font-semibold text-gray-900">{month}</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {monthHolidays.map((holiday) => (
                  <div key={holiday._id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                        <Star className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{holiday.name}</h4>
                        <p className="text-sm text-gray-500">{formatDate(holiday.date)}</p>
                        {holiday.description && (
                          <p className="text-xs text-gray-400 mt-1">{holiday.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getTypeColor(holiday.type)}>
                        {holiday.type}
                      </Badge>
                      <button
                        onClick={() => openEditModal(holiday)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        onClick={() => { setSelected(holiday); setIsDeleteModalOpen(true); }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-900 mb-1">No holidays for {currentYear}</h3>
          <p className="text-sm text-gray-500 mb-4">Add holidays to show them on the attendance calendar</p>
          <Button onClick={() => { resetForm(); setIsAddModalOpen(true); }}>
            Add Your First Holiday
          </Button>
        </Card>
      )}

      {/* Add Holiday Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Add New Holiday" size="md">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Holiday Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Diwali, Christmas"
            required
          />
          <Input
            label="Date"
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            required
          />
          <Select
            label="Holiday Type"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            {holidayTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Additional details about the holiday"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={createLoading}>Add Holiday</Button>
          </div>
        </form>
      </Modal>

      {/* Edit Holiday Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Edit Holiday" size="md">
        <form onSubmit={handleUpdate} className="space-y-4">
          <Input
            label="Holiday Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <Input
            label="Date"
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            required
          />
          <Select
            label="Holiday Type"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            {holidayTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={updateLoading}>Update Holiday</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Delete Holiday" size="sm">
        <div className="text-center">
          <Trash2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Are you sure you want to delete</p>
          <p className="font-semibold text-gray-900 mb-4">{selected?.name}?</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} loading={deleteLoading}>Delete</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HolidayManagement;
